import { useEffect, useRef } from "react";

import { AUTH0_CALLBACK_ERROR_EVENT_NAME } from "constants/auth0CallbackTelemetry";
import { logEvent } from "telemetry/appInsights";

/**
 * Record an Auth0 error that arrived on the redirect URL.
 *
 * Auth0 appends `error` and `error_description` to the callback when it sends the browser back
 * instead of completing the login. Reading them once on mount catches them before the router
 * replaces the URL, and before the SDK has finished deciding what to do with the transaction.
 *
 * This observes only the failures Auth0 redirects back from. When Auth0 renders its own error page
 * the browser never returns to the application, no app code runs, and the tenant log stream is the
 * only thing that records it.
 */
export const useAuth0CallbackErrorTelemetry = (): void => {
  const hasRecorded = useRef(false);

  useEffect(() => {
    // StrictMode runs an effect setup twice in development, and a double-counted failure is worse
    // than a missed one here: these events are counted to judge how often auth is breaking.
    if (hasRecorded.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const errorCode = params.get("error");
    if (!errorCode) {
      return;
    }

    hasRecorded.current = true;
    logEvent(AUTH0_CALLBACK_ERROR_EVENT_NAME, {
      errorCode: errorCode,
      errorDescription: params.get("error_description") ?? "",
      path: window.location.pathname,
    });
  }, []);
};
